import { SESSION_NAME } from '@/auth/constants/authSettings';
import { isKeychainAvailable, Keychain } from '@/utils/keychain';
import CryptoJS from 'crypto-js';

export type AuthSessionData = {
	isLoggedIn: boolean;

	// token
	accessToken?: string;
	expiresIn?: number;
	refreshToken?: string;
	refreshTokenExpiresIn?: number;

	// token claims
	userId?: number;
	userGroupId?: number;

	// additional data
	loginMethod?: 'email' | 'google';
	googleId?: string;
	preferredCurrency: string | undefined;
	preferredLang: string | undefined;
	email?: string;
};

const encrypt = (sessionData: AuthSessionData): string => {
	const json = JSON.stringify(sessionData);
	const cipherText = CryptoJS.AES.encrypt(json, import.meta.env.VITE_SESSION_SECRET);
	return cipherText.toString();
};

const decrypt = (cipherText: string): AuthSessionData => {
	const bytes = CryptoJS.AES.decrypt(cipherText, import.meta.env.VITE_SESSION_SECRET);
	const decryptedData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8)) satisfies AuthSessionData;
	return decryptedData;
};

/**
 * On iOS the session lives in the Keychain and this copy serves every read. The Keychain can only
 * be read asynchronously, so the module loads it before any importer runs; see the end of the file.
 */
const useKeychain = isKeychainAvailable();
let keychainSession: AuthSessionData | null = null;

const saveLocalSession = (session: AuthSessionData) => {
	try {
		const encryptedSession = encrypt(session);
		localStorage.setItem(SESSION_NAME, encryptedSession);
	} catch (error: unknown) {
		// Don't throw - failing to save session shouldn't break the app
		console.error('Failed to save session:', error);
	}
};

const getLocalSession = (): AuthSessionData | null => {
	try {
		const session = localStorage.getItem(SESSION_NAME);
		if (!session) {
			return null;
		}
		const decryptedSession = decrypt(session);
		return decryptedSession;
	} catch (error: unknown) {
		// If decryption fails, clear the corrupted session
		try {
			localStorage.removeItem(SESSION_NAME);
		} catch {
			// Ignore if we can't remove it
		}

		console.error('Failed to decrypt session, clearing corrupted data:', error);
		return null;
	}
};

const destroyLocalSession = () => {
	try {
		localStorage.removeItem(SESSION_NAME);
	} catch (error: unknown) {
		// Don't throw - this is not critical
		console.error('Failed to destroy session:', error);
	}
};

async function loadKeychainSession(): Promise<AuthSessionData | null> {
	let stored: string | null;
	try {
		({ value: stored } = await Keychain.get({ key: SESSION_NAME }));
	} catch (error: unknown) {
		console.error('Failed to read session from the Keychain:', error);
		return null;
	}

	if (stored) {
		// A copy can survive an earlier migration whose Keychain write failed.
		destroyLocalSession();
		try {
			return JSON.parse(stored) as AuthSessionData;
		} catch (error: unknown) {
			console.error('Failed to parse session from the Keychain:', error);
			return null;
		}
	}

	// Builds before the Keychain kept the session in localStorage; moving it means updating the app
	// doesn't sign anyone out.
	const legacySession = getLocalSession();
	if (legacySession) {
		try {
			await Keychain.set({ key: SESSION_NAME, value: JSON.stringify(legacySession) });
			destroyLocalSession();
		} catch (error: unknown) {
			console.error('Failed to move session to the Keychain:', error);
		}
	}
	return legacySession;
}

export const saveSession = (session: AuthSessionData) => {
	if (!useKeychain) {
		saveLocalSession(session);
		return;
	}

	keychainSession = { ...session };
	Keychain.set({ key: SESSION_NAME, value: JSON.stringify(session) }).catch((error: unknown) =>
		console.error('Failed to save session:', error),
	);
};

export const getSession = (): AuthSessionData | null => {
	if (!useKeychain) {
		return getLocalSession();
	}

	return keychainSession ? { ...keychainSession } : null;
};

export const destroySession = () => {
	if (!useKeychain) {
		destroyLocalSession();
		return;
	}

	keychainSession = null;
	Keychain.remove({ key: SESSION_NAME }).catch((error: unknown) => console.error('Failed to destroy session:', error));
};

if (useKeychain) {
	keychainSession = await loadKeychainSession();
}
